import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { Doughnut, Line } from "react-chartjs-2";
import { chartColors } from "../../../components/form/debtPayOff/components/color";
import insuranceIcon from "../../../assets/images/insurance.png";
import endingInsuranceIcon from "../../../assets/images/modules.png";
import highInsuranceIcon from "../../../assets/images/performance.png";
import "../InsuranceDashboard.css";
import getFAClientName from "../../../helpers/getFAClientName";
import { InsurancePDFReport } from "../../V2Insurance/components/InsurancePDFReport";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { MODULE_API } from "../../../apis";
import FETCH from "../../../utils/fetch";

export const InsuranceReport = React.forwardRef(({stats, totalPremium, onDownloadLoading}, ref) => {
  const [clientName, setClientName] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pdfInsuranceList, setPdfInsuranceList] = useState([]);
  const [chartImage, setChartImage] = useState(null);
  const chartRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    downloadPDF: () => {
      downloadPDF();
    },
    isDownloading: downloadLoading,
  }));

  const fetchAllInsuranceForPDF = async () => {
    let allData = [];
    let page = 1;
    let hasMore = true;
    const limit = 1000;
    const userRole = localStorage.getItem("role") != "undefined" && JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");

    while (hasMore) {
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id: userRole === "Financial Advisor Client"
            ? `?module=Insurance Products V2&page=${page}&limit=${limit}&clientId=${ID}`
            : `?module=Insurance Products V2&page=${page}&limit=${limit}`,
        body: {},
      });

      if (datares && datares.records && datares.records.length > 0) {
        allData = [...allData, ...datares.records];
        if (datares.records.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    return allData;
  };

  const downloadPDF = async () => {
    try {
      setDownloadLoading(true);
      if (onDownloadLoading) onDownloadLoading(true);

      // 1. CAPTURE CHART
      let capturedChartImage = null;
      for (let i = 0; i < 30; i++) {
        // Try gathering from live dashboard canvas first (faster and usually more reliable)
        const liveCanvas = document.querySelector('.insruance-info-main canvas');
        if (liveCanvas) {
          capturedChartImage = liveCanvas.toDataURL("image/png");
        }

        // Fallback to internal hidden chart if live canvas is blank or missing
        if (!capturedChartImage || capturedChartImage.length < 5000) {
          const chart = (chartRef.current && chartRef.current.chartInstance) || 
                        (chartRef.current && typeof chartRef.current.getChart === 'function' ? chartRef.current.getChart() : null);
          
          if (chart) {
            capturedChartImage = chart.toBase64Image();
          }
        }
        
        if (capturedChartImage && capturedChartImage.length > 5000) {
             break;
        }
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      setChartImage(capturedChartImage);

      // 2. FETCH ALL DATA
      const allInsurance = await fetchAllInsuranceForPDF();
      setPdfInsuranceList(allInsurance);

      // 3. GENERATE PDF
      setTimeout(async () => {
        const element = document.getElementById("insurance-report-pdf");
        if (!element) {
          setDownloadLoading(false);
          if (onDownloadLoading) onDownloadLoading(false);
          return;
        }

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
          removeContainer: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);

        const margin = 15;
        const contentWidth = pdfWidth - 2 * margin;
        const contentHeight = (imgProps.height * contentWidth) / imgProps.width;
        const pageHeight = pdfHeight - 2 * margin;

        let heightLeft = contentHeight;
        let position = margin;

        const addMargins = () => {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pdfWidth, margin, "F");
          pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F");
        };

        pdf.addImage(imgData, "PNG", margin, position, contentWidth, contentHeight);
        addMargins();
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          pdf.addPage();
          position = margin - (contentHeight - heightLeft);
          pdf.addImage(imgData, "PNG", margin, position, contentWidth, contentHeight);
          addMargins();
          heightLeft -= pageHeight;
        }

        pdf.save(`Insurance_Report_${moment().format("YYYYMMDD")}.pdf`);
        setDownloadLoading(false);
        if (onDownloadLoading) onDownloadLoading(false);
      }, 3500); // Increased wait time to 3.5s to assure React & Chart have fully calculated & painted massive datasets into DOM

    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
      if (onDownloadLoading) onDownloadLoading(false);
    }
  };

  const [chartDataForPDF, setChartDataForPDF] = useState(null);

  const getInsuarnceChartData = async () => {
    try {
      const datares = await MODULE_API.fetchInsuranceChart();
      if (datares && datares.data) {
        setChartDataForPDF(datares.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getInsuarnceChartData();
  }, []);

  const [clientNameLocal, setClientNameLocal] = useState("");
  useEffect(()=>{
    const name = getFAClientName();
    if (name) setClientNameLocal(name);
  },[])

  // Prepare chart data for hidden chart
  const allPolicies = chartDataForPDF && new Set();
  chartDataForPDF &&
    Object.values(chartDataForPDF).forEach((entries) => {
      entries &&
        entries.forEach((entry) => {
          Object.keys(entry).forEach(
            (key) => allPolicies && allPolicies.add(key)
          );
        });
    });

  const sortedMonths =
    chartDataForPDF &&
    Object.keys(chartDataForPDF).sort(
      (a, b) =>
        moment(a, "MMMM YYYY").toDate() - moment(b, "MMMM YYYY").toDate()
    );

  const chartLabels =
    sortedMonths &&
    sortedMonths.map((label) => moment(label, "MMMM YYYY").format("MMM"));

  const datasets =
    allPolicies &&
    Array.from(allPolicies).map((policyName, index) => {
      return {
        type: "bar",
        label: policyName,
        stack: "stack1",
        backgroundColor: chartColors[index % chartColors.length],
        data:
          sortedMonths &&
          sortedMonths.map((month) => {
            const entries = chartDataForPDF[month];
            const entry = entries && entries.find((e) => policyName in e);
            return entry ? parseFloat(entry[policyName]).toFixed(2) : 0;
          }),
      };
    });

  const finalChartData = {
    labels: chartLabels || [],
    datasets: datasets || [],
  };

  return (
    <React.Fragment>
      <div className="incomeCard1">
        <h3 className="Reportheading">Insurance</h3>
        <div className="incomeRow">
          <div>
            <div className="budget-home-icon-container" style={{ gap: "4px" }}>
              <div
                className="asset-home-icon"
                style={{ textAlign: "center", width: "48%" }}
              >
                <img
                  style={{ width: "100%", minWidth: "100px" }}
                  src={insuranceIcon}
                  alt="insuranceIcon"
                />
              </div>
              <div style={{ textAlign: "center", width: "49%" }}>
                <div className="incomeCardConatiner">
                  <h3
                    className="Reportheading"
                    style={{
                      fontSize: "16px",
                      fontWeight: "900",
                    }}
                  >
                  Monthly Premium
                  </h3>
                  <h3 className="Reportheading" style={{ fontWeight: "700" }}>
                    $
                    {totalPremium &&
                      totalPremium ?
                      totalPremium
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") :0}
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="reportrow">
              <div className="reportrowData">
                <span className="reportTitle"> Client Name</span>
                <span className="reportsubTitle">
                {clientNameLocal || localStorage.getItem("User") || ""}
                </span>
              </div>
              <div className="reportrowData" style={{alignItems:"end"}}>
                <span className="reportTitle"> Today's Date </span>
                <span className="reportsubTitle">
                  {" "}
                  {moment().format("MM/DD/YYYY")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="incomeCard2">
          <div className="budget-home-icon-container" style={{ gap: "4px" }}>
            <h3 className="incomeReportMainHaeding" style={{textAlign:"center", width:"50%"}}>Policies Ending Soon</h3>

            <div
              className="asset-home-icon"
              style={{ textAlign: "center", width: "35%" }}
            >
              <img
                style={{ width: "100%", minWidth: "100px" }}
                src={endingInsuranceIcon}
                alt="incomeIcon"
              />
            </div>
          </div>
          <div className="incomeRow">
            <div>
              {stats && stats.nearestPolicyEndDates && stats.nearestPolicyEndDates.map((item, index) => (
                <div className="IncomeRowMonth" style={{ marginTop: "1rem" }} key={index}>
                  <span className="IncomeRowMonthHeading">{item.policy_name}</span>
                  <span className="IncomeRowMonthValue">{item.policy_end_date ? moment(item.policy_end_date).format("MMM DD, YYYY") : "N/A"}</span>
                </div>
              )) 
              }
            </div>
          </div>
        </div>
      <div className="incomeCard2">
          <div className="budget-home-icon-container" style={{ gap: "4px" }}>
            <h3 className="incomeReportMainHaeding" style={{textAlign:"center", width:"50%"}}>Highest Monthly Premium</h3>

            <div
              className="asset-home-icon"
              style={{ textAlign: "center", width: "35%" }}
            >
              <img
                style={{ width: "100%", minWidth: "100px" }}
                src={highInsuranceIcon}
                alt="incomeIcon"
              />
            </div>
          </div>
          <div className="incomeRow">
            <div>
              {stats && stats.topPremiums && stats.topPremiums.map((item, index) => (
                <div className="IncomeRowMonth" style={{ marginTop: "1rem" }} key={index}>
                  <span className="IncomeRowMonthHeading">{item.policy_name}</span>
                  <span className="IncomeRowMonthValue">${item.premium_value ? item.premium_value.toFixed(2)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",") : 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      {/* Hidden container for PDF capture */}
      <div style={{ position: "fixed", top: 0, left: "-3000px", zIndex: -9999, opacity: 0.01, pointerEvents: "none" }}>
        <div style={{ width: "950px" }}>
          <Line
            ref={chartRef}
            data={finalChartData}
            options={{
              animation: { duration: 0 },
              responsive: false,
              maintainAspectRatio: false,
              scales: {
                xAxes: [{ stacked: true }],
                yAxes: [{ stacked: true }]
              }
            }}
            height={400}
            width={900}
          />
        </div>
        <div id="insurance-report-pdf">
          <InsurancePDFReport
            insuranceList={pdfInsuranceList}
            totalPremium={totalPremium}
            stats={stats}
            chartImage={chartImage}
            clientName={clientNameLocal || localStorage.getItem("User") || ""}
          />
        </div>
      </div>
    </React.Fragment>
  );
});
